import Component from '@glimmer/component';
import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { variation } from 'ember-launch-darkly';
import { eq, and, or, not } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import can from 'ember-can/helpers/can';
import cannot from 'ember-can/helpers/cannot';
import set from 'ember-set-helper/helpers/set';
import {
  HdsPageHeader,
  HdsDropdown,
  HdsLinkStandalone,
  HdsModal,
  HdsButton,
  HdsButtonSet,
} from '@hashicorp/design-system-components/components';
import {
  BreadcrumbsContainer,
  Typography,
  ModalConfirm,
} from 'core/components';
import { resourceIsBusy, resourceTypeFor } from 'core/helpers';
import { boxPadding } from 'core/modifiers';
import { TERRAFORM_IMPORTS } from 'core/utils/constants';
import {
  PEERING_ROUTE_NAMES,
  TGW_ROUTE_NAMES,
} from 'networks-common/utils/network-routes';
import CreateClusterButton from 'hcp/components/create-cluster-button';

/**
 *
 * `PageHvnsDetailHeader` renders the HashiCorp Virtual Networks Detail page.
 *
 *
 * ```
 * <Page::Hvns::Detail::Header
 *   @model={{@model}}
 *   @canCreateConnection={{@canCreateConnection}}
 *   @canCreatePeerings={{@canCreatePeerings}}
 *   @canCreateTgwAttachments={{@canCreateTgwAttachments}}
 * />
 * ```
 *
 * @class PageHvnsDetailHeader
 *
 */
export default class PageHvnsDetailHeader extends Component {
  /**
   *
   * `showModal` - whether or not the modal should show.
   * @argument showModal;
   * @type {Boolean}
   *
   */
  /**
   *
   * `showModalChanged` a function that gets called with true if the modal is open and false if it is closed.
   * @argument showModalChanged;
   * @type {Function}
   *
   */
  /**
   *
   * `deleteHVN` is the function to delete the HVN.
   * @argument deleteHVN;
   * @type {Function}
   *
   */
  /**
   *
   * `model` is an HVN object.
   * @argument model;
   * @type {Object}
   *
   */

  get terraformImport() {
    return TERRAFORM_IMPORTS.hvn({ id: this.args.model.network.id });
  }

  connectionRouteNames = {
    peeringRouteNames: PEERING_ROUTE_NAMES,
    tgwRouteNames: TGW_ROUTE_NAMES,
  };
  <template>
    <HdsPageHeader as |PH|>
      <PH.Breadcrumb>
        <BreadcrumbsContainer />
      </PH.Breadcrumb>
      <PH.Title>
        {{@model.network.id}}
      </PH.Title>
      {{#unless
        (resourceIsBusy @model.network type=(resourceTypeFor "network"))
      }}
        <PH.Actions>
          <HdsDropdown @listPosition="bottom-right" as |dd|>
            <dd.ToggleButton
              @color={{if @canCreateConnection "secondary" "primary"}}
              @text={{t "components.form.manage"}}
              data-test-manage-button
            />
            <dd.Title @text={{t "components.import-to-terraform.title"}} />
            <dd.Description @text={{t "components.import-to-terraform.body"}} />
            <dd.Generic>
              <div {{boxPadding "xs 0"}}>
                <HdsLinkStandalone
                  @href="https://www.terraform.io/docs/cli/import/usage.html"
                  @icon="docs-link"
                  @text={{t "components.import-to-terraform.docs-link"}}
                />
              </div>
            </dd.Generic>
            <dd.CopyItem @text={{this.terraformImport}} />
            {{#if (can "open delete modal in network")}}
              <dd.Separator />
              <dd.Interactive
                id="modal-dialog-open-button"
                @color="critical"
                @icon="trash"
                @text={{t "components.page.hvns.detail.manage-dropdown.delete"}}
                data-test-delete-button
                {{on "click" (fn @showModalChanged true)}}
              />
            {{/if}}
          </HdsDropdown>
          {{#if @canCreateConnection}}
            {{#if (variation "hcp-hvn-guided-flow")}}
              <HdsDropdown @listPosition="bottom-right" as |dd|>
                <dd.ToggleButton
                  @color="primary"
                  @text={{t
                    "components.page.hvns.detail.create-dropdown.guided-flow.title"
                  }}
                  data-test-create-connection-button
                  data-test-create-connection-button-header
                />
                {{#if @canCreatePeerings}}
                  <dd.Interactive
                    @text={{t
                      "components.page.hvns.detail.create-dropdown.guided-flow.peering.title"
                    }}
                    @route={{this.connectionRouteNames.peeringRouteNames.create}}
                    @query={{hash
                      project_id=@model.network.location.projectId
                      network_id=@model.network.id
                    }}
                    @icon="connection"
                    data-test-create-peering
                    {{on "click" @goToPeeringCreate}}
                  />
                  <dd.Description
                    @text={{t
                      "components.page.hvns.detail.create-dropdown.guided-flow.peering.description"
                      provider=@model.network.location.region.provider
                    }}
                  />
                {{/if}}
                {{#if (eq @model.network.location.region.provider "aws")}}
                  {{#if (and @canCreatePeerings @canCreateTgwAttachments)}}
                    <dd.Separator />
                  {{/if}}
                  {{#if @canCreateTgwAttachments}}
                    <dd.Interactive
                      @text={{t
                        "components.page.hvns.detail.create-dropdown.guided-flow.tgw.title"
                      }}
                      @route={{this.connectionRouteNames.tgwRouteNames.create}}
                      @query={{hash
                        project_id=@model.network.location.projectId
                        network_id=@model.network.id
                      }}
                      @icon="connection-gateway"
                      data-test-create-tgw
                      {{on "click" @goToTGWCreate}}
                    />
                    <dd.Description
                      @text={{t
                        "components.page.hvns.detail.create-dropdown.guided-flow.tgw.description"
                      }}
                    />
                  {{/if}}
                {{/if}}
              </HdsDropdown>
            {{else}}
              <CreateClusterButton @model={{@model}} data-test-create-button />
            {{/if}}
          {{/if}}
        </PH.Actions>
      {{/unless}}
    </HdsPageHeader>
    {{#if @showModal}}
      {{#if (cannot "delete network" @model)}}
        <HdsModal
          @color="critical"
          @onClose={{@showModalChanged}}
          data-test-delete-deps-modal
          as |M|
        >
          <M.Header @icon="x-square-fill">
            {{t "components.page.hvns.delete.cannot-title"}}
          </M.Header>
          <M.Body>
            <Typography
              @variant="body1"
              class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
            >
              {{t
                "components.page.hvns.delete.cannot-description"
                modelName=@model.network.id
                htmlSafe=true
              }}
            </Typography>
          </M.Body>
          <M.Footer>
            <HdsButton
              @color="secondary"
              @text={{t "components.form.close"}}
              data-test-delete-deps-modal-close
              {{on "click" (fn @showModalChanged false)}}
            />
          </M.Footer>
        </HdsModal>
      {{else}}
        <HdsModal @color="critical" @onClose={{@showModalChanged}} as |M|>
          <M.Header @icon="alert-triangle">
            {{t "components.page.hvns.delete.title"}}
          </M.Header>
          <M.Body>
            <form
              id="hvn-delete"
              class="hcp-hds-form"
              {{on "submit" @deleteHVN.perform}}
            >
              <div {{boxPadding "0 0 md 0"}}>
                <Typography
                  @variant="body1"
                  class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
                >
                  {{t
                    "components.page.hvns.delete.description"
                    modelName=@model.network.id
                    htmlSafe=true
                  }}
                </Typography>
              </div>
              <ModalConfirm @onAllowedChange={{set this "allowDelete"}} />
            </form>
          </M.Body>
          <M.Footer as |F|>
            <HdsButtonSet>
              <HdsButton
                @color="critical"
                @text={{t "components.form.delete"}}
                disabled={{or (not this.allowDelete) @deleteHVN.isRunning}}
                form="hvn-delete"
                type="submit"
                data-test-confirm-delete
              />
              <HdsButton
                @color="secondary"
                @text={{t "components.form.cancel"}}
                disabled={{@deleteHVN.isRunning}}
                {{on "click" F.close}}
              />
            </HdsButtonSet>
          </M.Footer>
        </HdsModal>
      {{/if}}
    {{/if}}
  </template>
}
